// src/AstexLeaderboard.js
import React, { useState, useMemo } from 'react';
import './Leaderboard.css';
import processAndSortCsvGeneral from './data.jsx';

// --- CONFIGURATION (remains the same) ---
const astexColumnMapping = {
  'Astex_1': 'RMSD<1Å',
  'Astex_2': 'RMSD<2Å',
  'Astex_1_pbvalid': 'RMSD<1Å & PB-valid',
  'Astex_2_pbvalid': 'RMSD<2Å & PB-valid',
  'Astex_1_relax': 'RMSD<1Å (Relaxed)',
  'Astex_2_relax': 'RMSD<2Å (Relaxed)',
  'Astex_1_pbvalid_relax': 'RMSD<1Å & PB-valid (Relaxed)',
  'Astex_2_pbvalid_relax': 'RMSD<2Å & PB-valid (Relaxed)',
};

const astexMetricsToExtract = [
  'Astex_1', 'Astex_2',
  'Astex_1_pbvalid', 'Astex_2_pbvalid',
  'Astex_1_relax', 'Astex_2_relax',
  'Astex_1_pbvalid_relax', 'Astex_2_pbvalid_relax'
];

const rmsdOptions = [
  { value: '1', label: 'RMSD<1Å' },
  { value: '2', label: 'RMSD<2Å' },
];

const pbValidOptions = [
  { value: 'none', label: 'None' },
  { value: 'pbvalid', label: 'PB-Valid' },
];

const relaxOptions = [
  { value: 'none', label: 'No' },
  { value: 'relax', label: 'Yes' },
];
// --- END CONFIGURATION ---


const AstexLeaderboard = () => {
  const [selectedRmsd, setSelectedRmsd] = useState('1'); // Example: default to RMSD<1
  const [selectedPbValid, setSelectedPbValid] = useState('pbvalid'); // Example: default to PB-valid
  const [selectedRelax, setSelectedRelax] = useState('none'); // Example: default to No Relax

  const derivedMetricKey = useMemo(() => {
    let key = `Astex_${selectedRmsd}`;
    if (selectedPbValid === 'pbvalid') {
      key += '_pbvalid';
    }
    if (selectedRelax === 'relax') {
      key += '_relax';
    }
    // Fallback to a known valid key, e.g., the first one or a common default
    return astexMetricsToExtract.includes(key) ? key : 'Astex_1_pbvalid';
  }, [selectedRmsd, selectedPbValid, selectedRelax]);

  const derivedMetricLabel = useMemo(() => {
    return astexColumnMapping[derivedMetricKey] || 'Selected Metric';
  }, [derivedMetricKey]);

  const leaderboardData = useMemo(() => {
    try {
      return processAndSortCsvGeneral(astexMetricsToExtract, derivedMetricKey);
    } catch (error) {
      console.error("Error processing CSV for Astex Leaderboard:", error);
      return [];
    }
  }, [derivedMetricKey]);

  const handleRmsdChange = (event) => {
    setSelectedRmsd(event.target.value);
  };

  const handlePbValidChange = (event) => {
    setSelectedPbValid(event.target.value);
  };

  const handleRelaxChange = (event) => {
    setSelectedRelax(event.target.value);
  };

  return (
    <div className="leaderboard-content-section">
      <div className="sort-options-grid">
        {/* Dropdown sections remain the same */}
        <div className="sort-option-item">
          <label htmlFor="sort-rmsd-astex">RMSD Threshold:</label>
          <select id="sort-rmsd-astex" value={selectedRmsd} onChange={handleRmsdChange} className="sort-dropdown">
            {rmsdOptions.map(option => (<option key={option.value} value={option.value}>{option.label}</option>))}
          </select>
        </div>
        <div className="sort-option-item">
          <label htmlFor="sort-pbvalid-astex">PB-Valid:</label>
          <select id="sort-pbvalid-astex" value={selectedPbValid} onChange={handlePbValidChange} className="sort-dropdown">
            {pbValidOptions.map(option => (<option key={option.value} value={option.value}>{option.label}</option>))}
          </select>
        </div>
        <div className="sort-option-item">
          <label htmlFor="sort-relax-astex">Relaxation:</label>
          <select id="sort-relax-astex" value={selectedRelax} onChange={handleRelaxChange} className="sort-dropdown">
            {relaxOptions.map(option => (<option key={option.value} value={option.value}>{option.label}</option>))}
          </select>
        </div>
      </div>

      <div className="leaderboard-table-container">
        <table className="leaderboard-table">
          <thead>
            <tr>
              <th className="rank-column"></th>
              <th className="model-header-column">Docking Model</th>
              <th className="metric-header-column">{derivedMetricLabel}</th>
            </tr>
          </thead>
          <tbody>
            {leaderboardData.length > 0 ? (
              leaderboardData.map((item) => (
                <tr key={item.rank + '-' + item.model}>
                  <td className="rank-column">{item.rank}</td>
                  <td className="model-data-column">
                    <div className="model-info">
                      <div className="model-name">{item.model}</div>
                    </div>
                  </td>
                  {/* REMOVE the span wrapper again, data will be left-aligned in the TD */}
                  <td className="metric-data-column">
                    {item[derivedMetricKey] !== undefined && item[derivedMetricKey] !== null && item[derivedMetricKey] !== ''
                      ? parseFloat(item[derivedMetricKey]).toFixed(3) + "%"
                      : 'N/A'}
                  </td>
                </tr>
              ))
            ) : (
              ""// ... (no data message) ...
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AstexLeaderboard;